/**
 * Funnel v2 pathology catalog — DISPLAY-ONLY keys for P8.
 *
 * NOTE: These keys intentionally differ from the legacy `Pathology` enum
 * values (e.g. 'digestion_sensible' here vs 'sensibilidad_digestiva' in
 * the enum). `mapCatalogKeyToPathologyEnum` bridges them before writing to
 * `DogFormData.pathologies` or sending to the backend.
 *
 * Tier 3 entries (cancer, epilepsia, cardiopatias, otra) have no Pathology
 * enum counterpart — they trigger an escalation modal and are NOT stored in
 * formData.pathologies.
 *
 * Spec: docs/superpowers/specs/2026-04-24-funnel-redesign-v2.md §Pantalla 8
 */
import { PathologyMeta, PathologyTier, Pathology } from '../../types';

export const PATHOLOGY_CATALOG: PathologyMeta[] = [
  // TIER 1 — adjust recipe/ration, no escalation modal.
  // 2026-04-27: diabetes/cancer/epilepsia/cardiopatias/otra moved here from
  // tier 2/3 — Santiago decided that only renal/hepática/pancreática warrant
  // pulling the user out of the funnel. The rest continue normally with
  // their condition recorded for the recipe selector to honour.
  {
    key: 'digestion_sensible',
    label: 'Digestión sensible',
    tier: 1,
    icon: '/images/iconos/enfermedades/sensibilidad_digestiva.png',
  },
  { key: 'piel_pelo', label: 'Piel / pelo / picor', tier: 1, icon: '/images/iconos/enfermedades/piel_pelo.png' },
  {
    key: 'articulaciones',
    label: 'Articulaciones / artritis',
    tier: 1,
    icon: '/images/iconos/enfermedades/problemas_articulares.png',
  },
  // 'sobrepeso' eliminado del listado: ya se pregunta en P4 (bodyCondition).
  // Mantenemos el catálogo abajo para compatibilidad con leads/DB antiguos
  // que tengan la patología guardada — el mapeo a Pathology.SOBREPESO_OBESIDAD
  // sigue activo si llega del backend.
  { key: 'diabetes', label: 'Diabetes', tier: 1, icon: '/images/iconos/enfermedades/diabetes.png' },
  { key: 'cancer', label: 'Cáncer', tier: 1, emoji: '🎗️' },
  { key: 'epilepsia', label: 'Epilepsia', tier: 1, icon: '/images/iconos/enfermedades/neurologico.png' },
  { key: 'cardiopatias', label: 'Cardiopatías', tier: 1, icon: '/images/iconos/enfermedades/cardiaco.png' },
  { key: 'otra', label: 'Otra', tier: 1, emoji: '❓' },

  // TIER 2 — show ATC popup, pull user out of automated flow.
  // Kept at the END of the list so users see the regular options first.
  { key: 'renal', label: 'Renal', tier: 2, icon: '/images/iconos/enfermedades/renal.png' },
  { key: 'hepatica', label: 'Hepática', tier: 2, icon: '/images/iconos/enfermedades/hepatica.png' },
  { key: 'pancreatica', label: 'Pancreática', tier: 2, icon: '/images/iconos/enfermedades/pancreatica.png' },
];

/**
 * Returns the informational copy shown to the user for a given pathology tier.
 *
 * The tier 1 copy contains a `{nombre}` placeholder that callers MUST
 * substitute with the dog's name before rendering to the user.
 */
export function getTierCopy(tier: PathologyTier): string {
  switch (tier) {
    case 1:
      return 'Ajustamos la receta para {nombre}.';
    case 2:
      return 'Para perros con esta condición, recomendamos consultar con tu veterinario antes de cambiar la alimentación.';
    case 3:
      return 'Aún no tenemos receta compatible para esta condición — la estamos preparando. Nuestras veterinarias pueden aconsejarte por WhatsApp.';
  }
}

export function pathologyByKey(key: string): PathologyMeta | undefined {
  return PATHOLOGY_CATALOG.find((p) => p.key === key);
}

/**
 * Insights de patologías — copy editable desde admin.
 *
 * Cada patología tiene una clave why (`p8.insight.{key}.why`) que explica
 * por qué nuestra comida ayuda y, opcionalmente, una clave supplement
 * (`p8.insight.{key}.supplement`) con el suplemento sugerido. Las claves
 * son `t()` lookups; los defaults aquí abajo (vía `getDefaultPathologyInsight`)
 * se usan si admin no ha personalizado el texto.
 *
 * Tier 2 (renal/hepatica/pancreatica) acompaña el formulario de waitlist
 * con copy explicando por qué bloqueamos.
 */
export interface PathologyInsight {
  why: string;
  supplement?: string;
}

const DEFAULT_PATHOLOGY_INSIGHTS: Record<string, PathologyInsight> = {
  digestion_sensible: {
    why: 'Proteína suave + prebióticos para la flora intestinal.',
    supplement: 'Probióticos y enzimas digestivas',
  },
  piel_pelo: {
    why: 'Omega-3 y vitamina E refuerzan manto y calman la piel.',
    supplement: 'Aceite de salmón / omega-3',
  },
  articulaciones: {
    why: 'Colágeno y proteína magra para sostener movilidad.',
    supplement: 'Glucosamina + condroitina',
  },
  sobrepeso: {
    why: 'Ración exacta y proteína magra: vuelve al peso ideal sin hambre.',
  },
  diabetes: {
    why: 'Carbohidratos controlados y fibra para estabilizar glucemia.',
    supplement: 'Fibra extra (psyllium)',
  },
  cancer: {
    why: 'Proteína, omega-3 y antioxidantes para apoyar el sistema inmune.',
    supplement: 'Aceite de salmón / omega-3',
  },
  epilepsia: {
    why: 'Omega-3 antiinflamatorios y micronutrientes neurológicos.',
    supplement: 'Aceite de salmón / omega-3',
  },
  cardiopatias: {
    why: 'Bajo sodio, taurina y omega-3 para la función cardíaca.',
    supplement: 'Taurina + omega-3',
  },
  otra: {
    why: 'Cuéntanoslo y nuestras vets confirman si encajamos.',
  },
  renal: {
    why: 'Necesita fósforo y proteína bajos — perfil aún en desarrollo.',
  },
  hepatica: {
    why: 'Requiere proteína muy digestible y poco cobre — receta en desarrollo.',
  },
  pancreatica: {
    why: 'Necesita poca grasa y proteína moderada — receta en desarrollo.',
  },
};

/**
 * Obtiene el insight final para una patología, dando prioridad a textos
 * personalizados desde admin (vía `t()`) sobre los defaults hardcodeados.
 *
 * @param key Clave de la patología (ej. 'renal', 'sobrepeso')
 * @param t Función de traducción de useAppConfig
 */
export function getPathologyInsight(
  key: string,
  t: (k: string, fallback: string) => string,
): PathologyInsight | undefined {
  const fallback = DEFAULT_PATHOLOGY_INSIGHTS[key];
  if (!fallback) return undefined;
  const why = t(`p8.insight.${key}.why`, fallback.why);
  const supplementDefault = fallback.supplement || '';
  const supplementValue = t(`p8.insight.${key}.supplement`, supplementDefault);
  return {
    why,
    supplement: supplementValue.trim() ? supplementValue : undefined,
  };
}

/** @deprecated mantener para tests legacy. Prefiere getPathologyInsight(). */
export const PATHOLOGY_INSIGHTS = DEFAULT_PATHOLOGY_INSIGHTS;

/**
 * Bridges catalog keys (display-only, P8) to the legacy Pathology enum
 * values that the backend understands.
 *
 * Returns null for catalog keys that have no Pathology enum counterpart:
 *  - 'cancer'       Tier 3 — user can't proceed anyway; don't write to formData
 *  - 'epilepsia'    Tier 3 — same
 *  - 'cardiopatias' Tier 3 — same
 *  - 'otra'         Tier 3 — same
 */
export function mapCatalogKeyToPathologyEnum(key: string): Pathology | null {
  switch (key) {
    case 'digestion_sensible':
      return Pathology.SENSIBILIDAD_DIGESTIVA;
    case 'piel_pelo':
      return Pathology.PIEL_PELO;
    case 'articulaciones':
      return Pathology.PROBLEMAS_ARTICULARES;
    case 'sobrepeso':
      return Pathology.SOBREPESO_OBESIDAD;
    case 'renal':
      return Pathology.RENAL;
    case 'hepatica':
      return Pathology.HEPATICA;
    case 'pancreatica':
      return Pathology.PANCREATICA;
    case 'diabetes':
      return Pathology.DIABETES;
    // Reincorporadas tras Codex review 2026-04-28: antes devolvían null
    // y la condición seleccionada se perdía en el persist. El backend
    // ahora también acepta estos valores en schemas/dog.py.
    case 'cancer':
      return Pathology.CANCER;
    case 'epilepsia':
      return Pathology.EPILEPSIA;
    case 'cardiopatias':
      return Pathology.CARDIOPATIAS;
    case 'otra':
      return Pathology.OTRA;
    default:
      return null;
  }
}
